import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { CHAIN_CONFIGS } from '../../utils/constants';
import { BridgeTransaction } from '../../types';
import { getExplorerTxUrl } from '../../utils/web3';

interface TransactionHistoryProps {
  transactions: BridgeTransaction[];
  onClearHistory?: () => void;
  onRefresh?: () => void;
}

export const TransactionHistory: React.FC<TransactionHistoryProps> = ({ 
  transactions, 
  onClearHistory,
  onRefresh
}) => {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  
  const hasTransactions = transactions.length > 0;

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const handleClearClick = () => {
    setShowClearConfirm(true);
  };

  const handleConfirmClear = () => {
    onClearHistory?.();
    setShowClearConfirm(false);
  };

  const handleCancelClear = () => {
    setShowClearConfirm(false);
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-900/40 text-yellow-300 border-yellow-700/30',
      processing: 'bg-blue-900/40 text-blue-300 border-blue-700/30 animate-pulse',
      completed: 'bg-green-900/40 text-green-300 border-green-700/30',
      failed: 'bg-red-900/40 text-red-300 border-red-700/30',
    };
    const labels = {
      pending: 'Pending',
      processing: 'Processing',
      completed: 'Completed',
      failed: 'Failed',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium backdrop-blur-sm border ${styles[status as keyof typeof styles] || ''}`}>
        {labels[status as keyof typeof labels] || status}
      </span>
    );
  };

  // Mobile Transaction Card Component
  const MobileTransactionCard = ({ tx }: { tx: BridgeTransaction }) => {
    const { date, time } = formatDate(tx.timestamp);
    return (
      <div className="bg-gray-800/30 rounded-xl p-4 border border-gray-700/30">
        {/* Header: Chains + Status */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <img 
              src={CHAIN_CONFIGS[tx.fromChain]?.icon} 
              alt={CHAIN_CONFIGS[tx.fromChain]?.name}
              className="w-6 h-6 rounded-full"
            />
            <svg className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
            <img 
              src={CHAIN_CONFIGS[tx.toChain]?.icon} 
              alt={CHAIN_CONFIGS[tx.toChain]?.name}
              className="w-6 h-6 rounded-full"
            />
          </div>
          {getStatusBadge(tx.status)}
        </div>
        
        {/* Amount */}
        <div className="text-lg font-semibold text-white mb-2">
          {tx.amount} {tx.token}
        </div>
        
        {/* Date + Actions */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">{date} • {time}</span>
          <div className="flex gap-2">
            <a
              href={getExplorerTxUrl(CHAIN_CONFIGS[tx.fromChain]?.chainId || '', tx.hash)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-400 hover:text-primary-300"
            >
              Source →
            </a>
            {tx.status === 'completed' && tx.toChainTxHash && (
              <a
                href={getExplorerTxUrl(CHAIN_CONFIGS[tx.toChain]?.chainId || '', tx.toChainTxHash)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-400 hover:text-green-300"
              >
                Dest →
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card 
      title="Transaction History" 
      subtitle="Your recent bridge transactions"
      className="mt-6"
    >
      {/* Refresh button */}
      {onRefresh && (
        <div className="mb-4 flex justify-end">
          <button
            onClick={onRefresh}
            className="text-xs sm:text-sm text-primary-400 hover:text-primary-300 transition-all duration-200 px-2 sm:px-3 py-1.5 rounded-lg bg-primary-900/20 hover:bg-primary-800/30 border border-primary-800/20 flex items-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      )}
      
      {hasTransactions ? (
        <>
          {/* Mobile View - Card Layout */}
          <div className="md:hidden space-y-3">
            {transactions.map((tx) => (
              <MobileTransactionCard key={tx.hash} tx={tx} />
            ))}
          </div>

          {/* Desktop View - Table Layout */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700/30">
              <thead className="bg-gray-800/30">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Route</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Links</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {transactions.map((tx) => {
                  const { date, time } = formatDate(tx.timestamp);
                  return (
                    <tr key={tx.hash} className="hover:bg-gray-700/20 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                        <div>{date}</div>
                        <div className="text-xs text-gray-500">{time}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <img src={CHAIN_CONFIGS[tx.fromChain]?.icon} alt="" className="w-5 h-5 rounded-full" />
                          <svg className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                          <img src={CHAIN_CONFIGS[tx.toChain]?.icon} alt="" className="w-5 h-5 rounded-full" />
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300 font-medium">
                        {tx.amount} {tx.token}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(tx.status)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs">
                        <div className="flex gap-2">
                          <a
                            href={getExplorerTxUrl(CHAIN_CONFIGS[tx.fromChain]?.chainId || '', tx.hash)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-400 hover:text-primary-300"
                          >
                            Source →
                          </a>
                          {tx.status === 'completed' && tx.toChainTxHash && (
                            <a
                              href={getExplorerTxUrl(CHAIN_CONFIGS[tx.toChain]?.chainId || '', tx.toChainTxHash)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-400 hover:text-green-300"
                            >
                              Dest →
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Clear History */}
          {onClearHistory && (
            <div className="mt-6 pt-4 border-t border-gray-700/30">
              {showClearConfirm ? (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-red-900/20 border border-red-800/30 rounded-lg p-3">
                  <p className="text-sm text-red-400">Clear all history?</p>
                  <div className="flex gap-2">
                    <button onClick={handleCancelClear} className="text-xs px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300">
                      Cancel
                    </button>
                    <button onClick={handleConfirmClear} className="text-xs px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-white">
                      Clear All
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={handleClearClick} className="text-xs text-gray-500 hover:text-red-400 transition-colors flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Clear History
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="py-8 text-center">
          <p className="text-gray-400 text-sm">No transactions yet. Bridge some tokens to get started!</p>
        </div>
      )}
    </Card>
  );
};
